/**
 * Client-side helpers for variant inheritance in the product editor.
 *
 * A variant inherits a fixed set of fields from its base product whenever its
 * own value is empty (mirrored from the server's productVariant service). These
 * pure helpers let the form decide, per field, whether the value is currently
 * inherited from the base or has been manually overridden on the variant.
 */

// Live emptiness checks, mirroring the server's isFieldEmpty semantics so the
// inherited/overridden badge stays accurate as the operator types.
export function isEmptyStr(v) {
  return v === null || v === undefined || String(v).trim() === '';
}

export function isEmptyArr(v) {
  return !Array.isArray(v) || v.length === 0;
}

export function isEmptyRange(min, max) {
  const empty = (x) => x === '' || x === null || x === undefined;
  return empty(min) && empty(max);
}

export function isEmptyNutrition(values) {
  return ['npProtein', 'npFat', 'npFiber', 'npAsh', 'npMoisture', 'npKcal', 'npLifestage']
    .every((k) => isEmptyStr(values[k]));
}

export function isEmptyReadingMap(map) {
  return !Object.values(map || {}).some((v) => String(v || '').trim());
}

/**
 * True when the base product has a non-empty value for an inheritable field, so
 * there is actually something to inherit (and the inheritance UI is meaningful).
 */
export function baseHas(base, field) {
  if (!base) return false;
  const v = base[field];
  switch (field) {
    case 'description':
    case 'brand':
      return !isEmptyStr(v);
    case 'ingredientLines':
    case 'ingredients':
    case 'analyticalConstituents':
    case 'additives':
    case 'speciesIds':
    case 'breedIds':
    case 'categoryIds':
      return !isEmptyArr(v);
    case 'ageRangeMonths':
    case 'weightRangeKg':
      return Boolean(v) && !isEmptyRange(v.min, v.max);
    case 'nutritionalProfile':
      return Boolean(v) && ['proteinPct', 'fatPct', 'fiberPct', 'ashPct', 'moisturePct', 'kcalPerKg', 'lifestage']
        .some((k) => v[k] !== null && v[k] !== undefined && v[k] !== '');
    case 'ormeLevel':
      return v !== null && v !== undefined && v !== '';
    case 'recipeReading':
    case 'plateReading':
      return Boolean(v && Array.isArray(v.sections)
        && v.sections.some((s) => s && s.body && s.body.text && String(s.body.text).trim()));
    default:
      return v !== null && v !== undefined && v !== '';
  }
}
